// src/services/api.js
import axios from "axios";

// Production API URL
export const API_BASE_URL = "https://pharmacy.melostechnologies.com/backend";

// Helper function to get token
const getToken = () => localStorage.getItem("token");

// Create Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add JWT token to every request if it exists
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle FormData for file uploads
api.interceptors.request.use(config => {
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']; // Let browser set boundary
  }
  return config;
});

// Handle License Errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && (error.response?.data?.error === "LICENSE_EXPIRED" || error.response?.data?.error === "LICENSE_INVALID")) {
      console.warn("🔒 License Issue detected:", error.response.data.message);
      // Optional: Redirect to license page if not already there
      if (!window.location.pathname.startsWith("/license")) {
        window.location.href = "/license";
      }
    }
    return Promise.reject(error);
  }
);

// ===== Auth =====
export const login = (credentials) => api.post("/users/login", credentials);
export const verifyToken = (token) =>
  token ? Promise.resolve() : Promise.reject(new Error("No token"));
export const resetUserPassword = (userId, newPassword) =>
  api.post("/users/reset-password", { userId, newPassword });

// ===== Reports =====
export const getDailySales = () => api.get("/reports/daily-sales");
export const getTopMedicines = (limit = 10) => api.get(`/reports/top-medicines?limit=${limit}`);
export const getLowStockAlerts = (threshold = 5) =>
  api.get(`/reports/low-stock?threshold=${threshold}`);
export const getExpiringSoonAlerts = (days = 30) =>
  api.get(`/reports/expiring-soon?days=${days}`);
export const getPrescriptionFulfillmentRate = () => api.get("/reports/prescription-rate");
export const getRevenueByCategory = () => api.get("/reports/revenue-by-category");

// ✅ FIXED: Updated to match backend route name
export const getReminderAnalytics = (days = 30) =>
  api.get(`/reports/reminder-analytics?days=${days}`);

// ✅ NEW: Comprehensive dashboard report
export const getComprehensiveReport = (params) =>
  api.get("/reports/comprehensive", { params });

// ✅ NEW: Report health check
export const getReportHealth = () => api.get("/reports/health");

// ✅ NEW: Chart data endpoints (you might want to add these later)
export const getNotificationTrends = (days = 7) =>
  api.get(`/reports/notification-trends?days=${days}`);

export const getSalesReport = (startDate, endDate) =>
  api.get(`/reports/sales-report`, { params: { startDate, endDate } });

export const getChannelPerformance = (startDate, endDate) =>
  api.get(`/reports/channel-performance`, { params: { startDate, endDate } });

// ===== ORDERS (UPDATED SECTION) =====
// ✅ FIXED: createOrder with proper error handling and debugging
export const createOrder = async (formData) => {
  try {
    console.log("🔄 Sending order request...");

    // Log FormData entries for debugging
    console.log("📤 FormData contents:");
    for (let [key, value] of formData.entries()) {
      if (key === 'prescriptionPhoto' && value instanceof File) {
        console.log(`${key}: File - ${value.name} (${value.size} bytes, type: ${value.type})`);
      } else if (key === 'items') {
        try {
          const parsedItems = JSON.parse(value);
          console.log(`${key}:`, parsedItems);
        } catch {
          console.log(`${key}: ${value.substring(0, 100)}...`);
        }
      } else {
        console.log(`${key}:`, value);
      }
    }

    // Send request
    const response = await api.post("/orders", formData, {
      // Headers will be set automatically for FormData
    });

    console.log("✅ Order created successfully:", response.data);
    return response;
  } catch (error) {
    console.error("❌ Order creation failed:");
    console.error("Status:", error.response?.status);
    console.error("Response data:", error.response?.data);
    console.error("Message:", error.message);

    // Extract error message from backend response
    const backendError = error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Order creation failed";

    throw new Error(backendError);
  }
};

// ✅ Debug upload endpoint
export const debugUpload = async (formData) => {
  try {
    console.log("🔍 Testing debug upload...");

    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value instanceof File ? `File: ${value.name}` : value);
    }

    const response = await api.post("/orders/debug-upload", formData, {});
    console.log("✅ Debug upload successful:", response.data);
    return response;
  } catch (error) {
    console.error("❌ Debug upload failed:", error.response?.data || error.message);
    throw error;
  }
};

export const getAllOrders = () => api.get("/orders");

export const getOrderById = (id) => api.get(`/orders/${id}`);

export const updateOrderStatus = (id, status) =>
  api.patch(`/orders/${id}/status`, { status });

export const getOrdersWithExceptions = (filters = {}) =>
  api.get("/orders/with-exceptions", { params: filters });

export const getTaxReport = (startDate, endDate) =>
  api.get("/orders/tax/report", { params: { startDate, endDate } });

export const getCosmeticsReport = (startDate, endDate) =>
  api.get("/orders/cosmetics/report", { params: { startDate, endDate } });

// ===== Medicines =====
export const searchMedicines = (params) => api.get("/medicine/search", { params });
export const createMedicine = (medicineData) => api.post("/medicine", medicineData);
export const getAllMedicines = () => api.get("/medicine");
export const getMedicineById = (id) => api.get(`/medicine/${id}`);
export const updateMedicine = (id, medicineData) => api.put(`/medicine/${id}`, medicineData);
export const deleteMedicine = (id) => api.delete(`/medicine/${id}`);
export const addInventoryToMedicine = (medicineId, inventoryData) =>
  api.post(`/medicine/${medicineId}/inventory`, inventoryData);
export const getLowStockMedicines = (threshold = 5) =>
  api.get(`/medicine/low-stock?threshold=${threshold}`);
export const getExpiringSoonMedicines = (days = 30) =>
  api.get(`/medicine/expiring-soon?days=${days}`);
// ✅ NEW: Drug Reference (public, no auth)
export const getDrugReference = (name) =>
  api.get(`/medicine/reference?name=${encodeURIComponent(name)}`);

// ===== Receipts =====
export const generateReceipt = (orderId) => api.get(`/receipts/${orderId}`);
export const downloadReceipt = (orderId) =>
  api.get(`/receipts/${orderId}/download`, { responseType: "blob" });

// ===== Prescriptions =====
export const getPendingPrescriptions = () => api.get("/prescriptions/pending");
export const fulfillPrescription = (prescriptionId) =>
  api.patch(`/prescriptions/${prescriptionId}/fulfill`);
export const cancelPrescription = (prescriptionId) =>
  api.patch(`/prescriptions/${prescriptionId}/cancel`);
export const createPrescription = (prescriptionData) =>
  api.post("/prescriptions", prescriptionData);
export const getCustomerPrescriptions = ({ name, phone }) => {
  return api.get("/prescriptions/search", {
    params: { name, phone }
  });
};
// ✅ NEW: Get by ID (enriched with patient/medicine)
export const getPrescriptionById = (id) => api.get(`/prescriptions/${id}`);

// ===== Patients (NEW) =====
export const registerPatient = (patientData) => api.post("/patients", patientData);
export const getAllPatients = () => api.get("/patients");
export const getPatientById = (id) => api.get(`/patients/${id}`);
export const updatePatient = (id, patientData) => api.put(`/patients/${id}`, patientData);
export const deletePatient = (id) => api.delete(`/patients/${id}`);

// ===== Notifications (NEW) =====
// Manual notification (pharmacist/admin)
export const sendManualNotification = (data) =>
  api.post("/notifications/manual", data);

// ===== Pharmacy Directive (NEW & WORKING) =====
// Upload (admin/superadmin)
export const uploadDirective = (formData) =>
  api.post("/directives/upload", formData);
// View in-browser (inline PDF)
export const viewDirective = () =>
  api.get("/directives/view", { responseType: "blob" });
// Download PDF
export const downloadDirective = () =>
  api.get("/directives/download", { responseType: "blob" });
// Get metadata
export const getDirectiveInfo = () => api.get("/directives/info");

// ===== Inventory Management =====
export const addBatch = (batchData) => api.post("/inventory/batch", batchData);
export const updateBatch = (id, batchData) => api.put(`/inventory/batch/${id}`, batchData);
export const getBatchesByMedicine = (medicineId) => api.get(`/inventory/medicine/${medicineId}`);
export const getBatchById = (id) => api.get(`/inventory/batch/${id}`);

// ===== User Management =====
export const createUser = (userData) => api.post("/users/register", userData);
export const changePassword = (passwordData) => api.put("/users/password", passwordData);
export const updateUserStatus = (id, status) =>
  api.patch(`/users/${id}/status`, { status });
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const getAllUsers = () => api.get("/users");

// ===== Supplier Management =====
export const createSupplier = (supplierData) => api.post("/suppliers", supplierData);
export const getAllSuppliers = () => api.get("/suppliers");
export const searchSuppliers = (name) => api.get(`/suppliers/search?name=${name}`);
export const getSupplierById = (id) => api.get(`/suppliers/${id}`);
export const updateSupplier = (id, supplierData) => api.put(`/suppliers/${id}`, supplierData);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`);

// Supplier Payments
export const getAllSupplierPayments = () => api.get("/supplier-payments");
export const getSupplierPaymentSummary = () => api.get("/supplier-payments/summary");
export const getSupplierPaymentById = (id) => api.get(`/supplier-payments/${id}`);
export const recordSupplierPayment = (id, data) => api.post(`/supplier-payments/${id}/pay`, data);


// ===== Audit Logs =====
export const getAuditLogs = () => api.get("/audit/logs");

// ===== AI Assistant =====
export const aiChat = (query) => api.post("/ai/medicine-chat", { query });

// ===== Roles & Menus =====
export const getAllRoles = () => api.get("/roles");
export const getUserRoles = (userId) => api.get(`/roles/user/${userId}`);
export const assignRoleToUser = (data) => api.post("/roles/assign", data);
export const removeRoleFromUser = (data) => api.post("/roles/remove", data);
export const getMenusByRole = (roleId) => api.get(`/roles/${roleId}/menus`);
export const fetchAllMenus = () => api.get("/menus/all");
export const assignMenuToRole = (data) => api.post("/menus/assign", data);
export const removeMenuFromRole = (data) => api.post("/menus/remove", data);

// ===== Grouped exports for scalability =====
export const reportsApi = {
  getDailySales,
  getTopMedicines,
  getLowStockAlerts,
  getExpiringSoonAlerts,
  getPrescriptionFulfillmentRate,
  getRevenueByCategory,
  getReminderAnalytics,          // ✅ UPDATED: Fixed endpoint name
  getComprehensiveReport,        // ✅ ADDED: Comprehensive dashboard
  getReportHealth,              // ✅ ADDED: Report health check
  getNotificationTrends,        // ✅ ADDED: For chart trends
  getChannelPerformance,         // ✅ ADDED: Channel performance
  getSalesReport                // ✅ ADDED: Date range sales report
};

export const ordersApi = {
  create: createOrder,
  debugUpload: debugUpload, // Added debug endpoint
  getAll: getAllOrders,
  getById: getOrderById,
  updateStatus: updateOrderStatus,
  getWithExceptions: getOrdersWithExceptions,
  getTaxReport: getTaxReport,
  getCosmeticsReport: getCosmeticsReport
};

export const medicinesApi = {
  search: searchMedicines,
  reference: getDrugReference,
  getAll: getAllMedicines,
  getById: getMedicineById,
  create: createMedicine,
  update: updateMedicine,
  delete: deleteMedicine,
  addInventory: addInventoryToMedicine,
  lowStock: getLowStockMedicines,
  expiringSoon: getExpiringSoonMedicines
};

export const receiptsApi = {
  generate: generateReceipt,
  download: downloadReceipt
};

export const prescriptionsApi = {
  getPending: getPendingPrescriptions,
  fulfill: fulfillPrescription,
  cancel: cancelPrescription,
  create: createPrescription,
  search: getCustomerPrescriptions,
  getById: getPrescriptionById
};

export const patientsApi = {
  register: registerPatient,
  getAll: getAllPatients,
  getById: getPatientById,
  update: updatePatient,
  delete: deletePatient
};

export const notificationsApi = {
  sendManual: sendManualNotification,
  getAnalytics: getReminderAnalytics // ✅ ADDED: For easy access
};

export const directivesApi = {
  upload: uploadDirective,
  view: (id) => api.get(`/directives/view${id ? `?id=${id}` : ""}`, { responseType: "blob" }),
  download: (id) => api.get(`/directives/download${id ? `?id=${id}` : ""}`, { responseType: "blob" }),
  info: getDirectiveInfo,
  history: () => api.get("/directives/history")
};

export const inventoryApi = {
  lowStock: getLowStockAlerts,
  expiringSoon: getExpiringSoonAlerts,
  addBatch,
  updateBatch,
  getBatchesByMedicine,
  getBatchById
};

export const usersApi = {
  getAll: getAllUsers,
  create: createUser,
  changePassword,
  updateStatus: updateUserStatus,
  delete: deleteUser
};

export const suppliersApi = {
  getAll: getAllSuppliers,
  create: createSupplier,
  search: searchSuppliers,
  getById: getSupplierById,
  update: updateSupplier,
  delete: deleteSupplier
};

export const auditApi = {
  getLogs: getAuditLogs
};

export const aiApi = {
  chat: aiChat
};

export const licenseApi = {
  getStatus: () => api.get("/api/license/status"),
  upload: (formData) => api.post("/api/license/upload", formData)
};

export const rolesApi = {
  getAll: getAllRoles,
  getUserRoles,
  assign: assignRoleToUser,
  remove: removeRoleFromUser,
  getMenusByRole,
  assignMenu: assignMenuToRole,
  removeMenu: removeMenuFromRole,
  getAllMenus: fetchAllMenus
};

export const settingsApi = {
  getTax: () => api.get("/settings/tax"),
  updateTax: (data) => api.post("/settings/tax", data),
  getAll: (category) => api.get(`/settings/all${category ? `?category=${category}` : ""}`),
  getCompany: () => api.get("/settings/company"),
  updateCompany: (data) => api.post("/settings/company", data)
};

export default api;